import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocPortal, MapLocKeys } from '@cm/core/localization';
import { BadgeDetailViewLocs } from 'libs/cp/ui/src/lib/components/badge-detail-view/badge-detail-view.locs';

const dpRoot = 'Public|Badge';

const sharedLocDefs: Pick<LocDef, 'feature' | 'htmlAllowed' | 'shared' | 'portal'> = {
	feature: HubFeature.ShareCertMetricsBadging,
	htmlAllowed: false,
	shared: false,
	portal: 'CP' as LocPortal,
};

type CPPublicBadgePageLocKeys = 'CPPublicBadgeNotFound' | 'CPPublicBadgeCriteriaButton';

export const cpPublicBadgePageLocs: LocDefs<CPPublicBadgePageLocKeys> = {
	CPPublicBadgeNotFound: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.NotFound',
		defaultValue: 'Badge not found',
		description: 'Message displayed when the badge is not found',
		displayPath: `${dpRoot}|Badge Not Found`,
		displayType: 'Content',
		...sharedLocDefs,
	},
	CPPublicBadgeCriteriaButton: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.CriteriaButton',
		defaultValue: 'Criteria',
		description: 'Button to view criteria on public badge',
		displayPath: `${dpRoot}|Criteria`,
		displayType: 'Button',
		...sharedLocDefs,
	},
};

export type PublicBadgeDetailLocKeys =
	| 'CPPublicBadgeContentImageAltTag'
	| 'CPPublicBadgeContentVisit'
	| 'CPPublicBadgeLabelOrganization'
	| 'CPPublicBadgeLabelUrl'
	| 'CPPublicBadgeTitleBadgeDetails'
	| 'CPPublicBadgeLabelIssuedTo'
	| 'CPPublicBadgeLabelIssuer'
	| 'CPPublicBadgeLabelViews'
	| 'CPPublicBadgeLabelDateEarned'
	| 'CPPublicBadgeLabelLastAdjustmentDate'
	| 'CPPublicBadgeLabelValidThrough'
	| 'CPPublicBadgeLabelCriteria'
	| 'CPPublicBadgeLabelDescription';

export const cpPublicBadgeDetailLocs: LocDefs<PublicBadgeDetailLocKeys> = {
	CPPublicBadgeContentImageAltTag: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.ImageInfo.BadgeAltTag',
		defaultValue: 'Badge logo',
		description: 'Badge image text used for accessibility',
		displayPath: `${dpRoot}|Issuer Details`,
		displayType: 'Content',
		...sharedLocDefs,
	},
	CPPublicBadgeLabelDescription: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.FieldLabel.Description',
		defaultValue: 'Description',
		description: 'Label for the description field',
		displayPath: `${dpRoot}|Description`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPPublicBadgeLabelDateEarned: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.FieldLabel.DateEarned',
		defaultValue: 'Date Earned',
		description: 'Label for the active date field',
		displayPath: `${dpRoot}|Date Earned`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPPublicBadgeLabelIssuedTo: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.FieldLabel.IssuedTo',
		defaultValue: 'Issued To',
		description: 'Label for the issued to field',
		displayPath: `${dpRoot}|Issued To`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPPublicBadgeLabelIssuer: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.FieldLabel.Issuer',
		defaultValue: 'Issuer Details',
		description: 'Label for the issuers field',
		displayPath: `${dpRoot}|Issuer`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPPublicBadgeLabelLastAdjustmentDate: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.FieldLabel.LastAdjustmentDate',
		defaultValue: 'Last Extension Date',
		description: 'Label for the last adjustment date',
		displayPath: `${dpRoot}|Last Extension Date`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPPublicBadgeLabelValidThrough: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.FieldLabel.ValidThrough',
		defaultValue: 'Valid Through',
		description: 'Label for the valid through field',
		displayPath: `${dpRoot}|Valid Through`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPPublicBadgeTitleBadgeDetails: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.Title.PageTitle',
		defaultValue: 'Badge',
		description: 'Page title for the badge sharing page.',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		...sharedLocDefs,
	},
	CPPublicBadgeLabelOrganization: {
		legacyName: 'Share.CertMetricsBadging/Share.Badges.Details.FieldLabel.Organization',
		defaultValue: 'Issuer',
		description: 'The field label for the issuing organization',
		displayPath: `${dpRoot}|Organization`,
		displayType: 'Title',
		...sharedLocDefs,
	},
	CPPublicBadgeLabelUrl: {
		legacyName: 'Share.CertMetricsBadging/Share.Badges.Details.FieldLabel.Url',
		defaultValue: 'URL',
		description: 'The field label for the url to the issuing organization',
		displayPath: `${dpRoot}|URL`,
		displayType: 'Title',
		...sharedLocDefs,
	},
	CPPublicBadgeContentVisit: {
		defaultValue: 'Visit',
		description: 'Content shown for visiting a different page',
		displayPath: `${dpRoot}|Visit`,
		displayType: 'Content',
		...sharedLocDefs,
	},
	CPPublicBadgeLabelViews: {
		legacyName: 'Share.CertMetricsBadging/Share.Badges.Details.FieldLabel.ViewCount',
		defaultValue: 'Views',
		displayPath: `${dpRoot}|Views`,
		description: 'The field label for the number of badge views',
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPPublicBadgeLabelCriteria: {
		legacyName: 'Share.CertMetricsBadging/Share.Badges.Details.FieldLabel.Criteria',
		defaultValue: 'Criteria',
		displayPath: `${dpRoot}|Criteria`,
		description:
			'The field label for the criteria url. This url will link to the page that provides the criteria for obtaining the badge',
		displayType: 'Label',
		...sharedLocDefs,
	},
};

export const BADGE_DETAIL_LOC_KEYS = MapLocKeys<PublicBadgeDetailLocKeys>(cpPublicBadgeDetailLocs);
export const LOC_KEYS = MapLocKeys<CPPublicBadgePageLocKeys>(cpPublicBadgePageLocs);

export const mapBadgeDetailLocs: BadgeDetailViewLocs = {
	BadgeDetailViewContentImageAltTag: BADGE_DETAIL_LOC_KEYS.CPPublicBadgeContentImageAltTag,
	BadgeDetailViewContentVisit: BADGE_DETAIL_LOC_KEYS.CPPublicBadgeContentVisit,
	BadgeDetailViewLabelOrganization: BADGE_DETAIL_LOC_KEYS.CPPublicBadgeLabelOrganization,
	BadgeDetailViewLabelUrl: BADGE_DETAIL_LOC_KEYS.CPPublicBadgeLabelUrl,
	BadgeDetailViewTitleBadgeDetails: BADGE_DETAIL_LOC_KEYS.CPPublicBadgeTitleBadgeDetails,
	BadgeDetailViewLabelIssuedTo: BADGE_DETAIL_LOC_KEYS.CPPublicBadgeLabelIssuedTo,
	BadgeDetailViewLabelIssuer: BADGE_DETAIL_LOC_KEYS.CPPublicBadgeLabelIssuer,
	BadgeDetailViewLabelViews: BADGE_DETAIL_LOC_KEYS.CPPublicBadgeLabelViews,
	BadgeDetailViewLabelDateEarned: BADGE_DETAIL_LOC_KEYS.CPPublicBadgeLabelDateEarned,
	BadgeDetailViewLabelLastAdjustmentDate:
		BADGE_DETAIL_LOC_KEYS.CPPublicBadgeLabelLastAdjustmentDate,
	BadgeDetailViewLabelValidThrough: BADGE_DETAIL_LOC_KEYS.CPPublicBadgeLabelValidThrough,
	BadgeDetailViewLabelCriteria: BADGE_DETAIL_LOC_KEYS.CPPublicBadgeLabelCriteria,
	BadgeDetailViewLabelDescription: BADGE_DETAIL_LOC_KEYS.CPPublicBadgeLabelDescription,
};
